import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Tabs, Tab } from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import WheelSettings from './WheelSettings/WheelSettings';
// import SpinConfig from './SpinConfig/SpinConfig';
import PointConfig from './PointConfig/PointConfig';
import GameComposer from './GameComposer/GameComposer';
import PaymentsTab from './Payments/PaymentsTab';
import BonusSettings from './BonusSettings/BonusSettings';
import BonusesList from './BonusesList/BonusesList';
import LevelSettings from './LevelSettings/LevelSettings';
import GeoTab from './GeoTab/GeoTab';
import LangTab from './LangTab/LangTab';
import BonusFreeSpinsTab from './BonusFreeSpins/BonusFreeSpinsTab';
import WheelTab from './WheelTab/WheelTab';
import WagerConfig from './WagerConfig/WagerConfig';
import styles from './siteDetail.module.scss';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`site-tabpanel-${index}`}
      aria-labelledby={`site-tab-${index}`}
      {...other}
      style={{
        flex: 1,
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {value === index && (
        <Box
          sx={{
            p: 3,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

const SiteDetail: React.FC = () => {
  const { id: siteId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Button
          startIcon={<ChevronLeft size={20} />}
          onClick={() => navigate('/sites')}
          className={styles.backBtn}
        >
          Back to Sites
        </Button>
      </Box>

      <Paper className={styles.contentCard}>
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="site detail tabs"
            className={styles.tabs}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{
              style: { backgroundColor: 'var(--accent-blue)' },
            }}
            sx={{
              '& .MuiTabScrollButton-root': {
                color: 'var(--text-primary)',
              },
            }}
          >
            <Tab label="Bonuses" className={styles.tab} />
            <Tab label="Bonus settings" className={styles.tab} />
            <Tab label="Bonus Free Spins" className={styles.tab} />
            <Tab label="Level Wheel" className={styles.tab} />
            <Tab label="Level Settings" className={styles.tab} />
            <Tab label="Point Config" className={styles.tab} />
            <Tab label="Wager Config" className={styles.tab} />
            <Tab label="Wheel" className={styles.tab} />
            <Tab label="Game Composer" className={styles.tab} />
            <Tab label="Payments" className={styles.tab} />
            <Tab label="Geo" className={styles.tab} />
            <Tab label="Lang" className={styles.tab} />
          </Tabs>
        </Box>

        <CustomTabPanel value={tabValue} index={0}>
          <BonusesList siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1}>
          <BonusSettings siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2}>
          <BonusFreeSpinsTab siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3}>
          <WheelSettings siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={4}>
          {/* <SpinConfig siteId={siteId!} /> */}
          <LevelSettings siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={5}>
          <PointConfig siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={6}>
          <WagerConfig siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={7}>
          <WheelTab siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={8}>
          <GameComposer />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={9}>
          <PaymentsTab siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={10}>
          <GeoTab siteId={siteId!} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={11}>
          <LangTab siteId={siteId!} />
        </CustomTabPanel>
      </Paper>
    </Box>
  );
};

export default SiteDetail;
